/*eslint complexity: ["error", 30]*/
/*eslint max-lines-per-function: ["error", 180]*/
import React from 'react';
import * as _ from 'lodash';
import * as Icon from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
  GridRecord,
  GridSummaryFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetPurchaseInvoiceExtendedDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import {
  InvoiceTypeEnum,
  InvoiceTypeObj,
  PurchaseInvoiceStatusObj,
} from '../../../common/pokCore/validation/enums';
import { DownloadAttachment } from '../../../common/components/File/DownloadAttachment';

import { NewWindowButton } from './NewWindowButton';
import { PurchaseInvoiceAmountOnGrid } from './PurchaseInvoiceAmountOnGrid';

interface PurchaseInvoiceSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  getSummary?: GridSummaryFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  displayStatus?: boolean;
  displayCurrency?: boolean;
  displayLastNote?: boolean;
  displayTeamsProjects?: boolean;
  displayCreator?: boolean;
  mode?: 'estimateItem' | 'project' | 'finance';
  enableMultiSelect?: boolean;
  withFilterInColumns?: boolean;
  showNewWindowButton?: boolean;
  withPreview?: boolean;
  withCountDays?: boolean;
  withPaidInfo?: boolean;
  enableSingleSelect?: boolean;
  selectedKeys?: string[];
}

export const purchaseInvoiceForGrid = (
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  purchaseInvoice: GetPurchaseInvoiceExtendedDto,
  orderBy: string = '',
) => {
  const projectProfileNames = _.uniq(
    purchaseInvoice.projects
      ?.map(({ estimatesByMonths }) =>
        estimatesByMonths?.map(estimate => estimate.profile.name),
      )
      .flat(),
  );
  const projectTeamsNames = _.uniq(
    purchaseInvoice.teams?.map(team => team.name),
  );
  let projectNumbers = _.uniq(
    purchaseInvoice.projects?.map(
      ({ name, number }) => name + ' (' + number + ')',
    ),
  );
  let projectOwners = _.uniq(
    purchaseInvoice.projects?.map(
      ({ responsibleOwner }) => responsibleOwner?.name,
    ),
  );
  if (projectNumbers && orderBy !== 'project.number') {
    projectNumbers = projectNumbers.sort((a, b) => a.localeCompare(b, 'pl'));
  }
  if (projectOwners && orderBy !== 'project.responsibleOwner') {
    projectOwners = projectOwners.sort((a, b) => a.localeCompare(b, 'pl'));
  }

  return {
    key: purchaseInvoice.id,
    values: {
      invoice_status: t(PurchaseInvoiceStatusObj[purchaseInvoice.status]),
      'purchaseInvoice.systemNumber': purchaseInvoice.systemNumber,
      'purchaseInvoice.type': t(InvoiceTypeObj[purchaseInvoice.type]),
      'purchaseInvoice.number': (
        <>
          {purchaseInvoice.number}
          {purchaseInvoice.number && <br />}
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>{t(InvoiceTypeObj[purchaseInvoice.type])}</Tooltip>
            }
          >
            {purchaseInvoice.type === InvoiceTypeEnum.Foreign ? (
              <Icon.Globe2 className="icon-blue me-2" />
            ) : (
              <Icon.House className="icon-blue me-2" />
            )}
          </OverlayTrigger>
          {purchaseInvoice.tv && (
            <OverlayTrigger placement="bottom" overlay={<Tooltip>TV</Tooltip>}>
              {purchaseInvoice.tv && (
                <Icon.DisplayFill className="icon-blue me-2" />
              )}
            </OverlayTrigger>
          )}
          {!!purchaseInvoice.originalInvoice && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  {t(tk.finance.correctionToInvoice)}{' '}
                  {purchaseInvoice.originalInvoice?.number}
                  {!!purchaseInvoice?.originalInvoice?.projects?.length && (
                    <>
                      <br />
                      {t(tk.finance.budgetsOfCorrectedInvoice)}:{' '}
                      {_.uniq(
                        purchaseInvoice.originalInvoice.projects
                          .map(o => o.purchaser?.budget?.name)
                          .filter(Boolean),
                      ).join(', ')}
                    </>
                  )}
                </Tooltip>
              }
            >
              <Icon.FileEarmarkMinusFill className="icon-blue me-2" />
            </OverlayTrigger>
          )}
        </>
      ),
      'purchaseInvoice.sellDate': momencik(purchaseInvoice.sellDate),
      'purchaseInvoice.creationTs': momencik(purchaseInvoice.creationTs),
      'purchaseInvoice.issueDate': momencik(purchaseInvoice.issueDate),
      'purchaseInvoice.comment': purchaseInvoice.comment,
      'purchaseInvoice.currency': purchaseInvoice.currency,
      'client.name': purchaseInvoice.client.name,
      'budget.name': _.uniq(
        purchaseInvoice.projects?.map(o => o.purchaser?.budget?.name),
      )?.join(', '),

      project_label: projectNumbers?.join(', '),
      'creator.name': purchaseInvoice.creator?.name,
      'responsibleOwner.name': projectOwners?.join(', '),
      'profile.name': projectProfileNames?.join(', '),
      'team.name': projectTeamsNames?.join(', '),
      'purchaseInvoice.amount': (
        <>{numberFormatter(purchaseInvoice.amount, true)}</>
      ),
      invoice_paid: (
        <>
          <PurchaseInvoiceAmountOnGrid paid={purchaseInvoice.paid} />
        </>
      ),
      'purchaseInvoice.amountWithoutPosition': numberFormatter(
        purchaseInvoice.amountWithoutPosition,
        true,
      ),
      'attachment.note': purchaseInvoice.note,
      count_days_from_creation: purchaseInvoice.countDaysFromCreation,
      newWindowButton: (
        <NewWindowButton purchaseInvoiceId={purchaseInvoice.id} />
      ),
      preview: purchaseInvoice.attachmentId ? (
        <DownloadAttachment
          id={purchaseInvoice.attachmentId}
          icon={Icon.FilePdf}
        />
      ) : (
        <></>
      ),
    },
    isInactive: !purchaseInvoice.active,
    tdCssClass: {
      'purchaseInvoice.amount': 'amounts-align-right',
      'purchaseInvoice.amountWithoutPosition': 'amounts-align-right',
    },
  };
};

export const getSummary = (
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  data: GetPurchaseInvoiceExtendedDto,
) => {
  return {
    key: 'summary',
    values: {
      'purchaseInvoice.systemNumber': t(tk.common.sumUpperCase),
      'purchaseInvoice.amountWithoutPosition': numberFormatter(
        data.amountWithoutPosition || 0,
        true,
      ),
      'purchaseInvoice.amount': numberFormatter(data.amount || 0, true),
    },
    tdCssClass: {
      'purchaseInvoice.amount': 'amounts-align-right',
      'purchaseInvoice.amountWithoutPosition': 'amounts-align-right',
    },
  } as unknown as GridRecord;
};

export const PurchaseInvoiceSearch: React.FC<
  PurchaseInvoiceSearchProps
> = props => {
  const { t, tk } = useTranslation('fvzLng');

  const columns: GridColumn[] = [
    ...(props.showNewWindowButton
      ? [
          {
            header: '',
            property: 'newWindowButton',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
    {
      header: t(tk.common.sysNumber),
      property: 'purchaseInvoice.systemNumber',
    },
    { header: t(tk.finance.invoiceNumber), property: 'purchaseInvoice.number' },
    { header: t(tk.common.issueDate), property: 'purchaseInvoice.issueDate' },
    { header: t(tk.common.saleDate), property: 'purchaseInvoice.sellDate' },
    {
      header: t(tk.finance.client),
      property: 'client.name',
    },
    ...(props.displayCurrency
      ? [
          {
            header: t(tk.common.currency),
            property: 'purchaseInvoice.currency',
          },
        ]
      : []),
    ...(props.displayStatus
      ? [
          {
            header: t(tk.common.status),
            property: 'invoice_status',
          },
        ]
      : []),
    ...(props.displayTeamsProjects
      ? [
          {
            header: t(tk.prLang.project),
            property: 'project_label',
          },
        ]
      : []),
    ...(props.mode === 'estimateItem'
      ? [
          {
            header: t(tk.common.budget),
            property: 'budget.name',
          },
        ]
      : []),
    ...(props.mode === 'estimateItem'
      ? [
          {
            header: t(tk.finance.activeProfiles),
            property: 'profile.name',
            noSorting: true,
          },
          {
            header: `${t(tk.common.creator)} ${t(tk.prLang.genitiveProject)}`,
            property: 'responsibleOwner.name',
          },
        ]
      : []),
    ...(props.displayTeamsProjects
      ? [
          {
            header: t(tk.common.team),
            property: 'team.name',
          },
        ]
      : []),
    {
      header: t(tk.attachment.comment),
      property: 'purchaseInvoice.comment',
    },
    ...(props.withPaidInfo
      ? [
          {
            header: t(tk.invoice.paid),
            property: 'invoice_paid',
          },
        ]
      : []),
    {
      header: t(tk.finance.invoiceAmount),
      property: 'purchaseInvoice.amount',
      noFilter: true,
    },
    {
      header: t(tk.finance.unsettledAmount),
      property: 'purchaseInvoice.amountWithoutPosition',
      noFilter: true,
    },
    ...(props.displayCreator
      ? [
          {
            header: t(tk.finance.created),
            property: 'creator.name',
          },
        ]
      : []),
    ...(props.withCountDays
      ? [
          {
            header: t(tk.common.creationDate),
            property: 'purchaseInvoice.creationTs',
          },
          {
            header: t(tk.finance.countDaysFromCreation),
            property: 'count_days_from_creation',
            noFilter: true,
          },
        ]
      : []),
    ...(props.displayLastNote
      ? [
          {
            header: t(tk.attachment.note),
            property: 'attachment.note',
          },
        ]
      : []),
    ...(props.withPreview
      ? [
          {
            header: t(tk.attachment.attachment),
            property: 'preview',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      getSummaryData={props.getSummary}
      columns={columns}
      showFilter={true}
      refresh={props.refresh}
      initialOrderColumn="purchaseInvoice.systemNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.finance.noInvoices)}
      onRowClick={props.onRowClick}
      withHistory={true}
      enableMultiSelect={props.enableMultiSelect}
      withFilterInColumns={props.withFilterInColumns}
      withOverflowXAuto={true}
      enableSingleSelect={props.enableSingleSelect}
      selectedKeys={props.selectedKeys}
    />
  );
};
